import CryptoJS from 'crypto-js';
import { DOCUMENT_VIEWER_CONFIG } from '../utils/constants';

class ChunkDecryptionService {
  // ---- helpers ----
  static _b64urlToB64(b64url) {
    let s = (b64url || '').replace(/-/g, '+').replace(/_/g, '/');
    const pad = s.length % 4;
    if (pad === 2) s += '==';
    else if (pad === 3) s += '=';
    else if (pad !== 0) throw new Error('B64URL_INVALID_LEN');
    return s;
  }
  static _b64ToWA(b64) { return CryptoJS.enc.Base64.parse(b64); }

  static _waToU8(wa) {
    const { words, sigBytes } = wa;
    const u8 = new Uint8Array(sigBytes);
    for (let i = 0; i < sigBytes; i++) u8[i] = (words[i >>> 2] >>> (24 - (i % 4) * 8)) & 0xff;
    return u8;
  }
  static _u8ToWA(u8) {
    const words = [];
    for (let i = 0; i < u8.length; i += 4) {
      words.push(
        ((u8[i] || 0) << 24) |
        ((u8[i + 1] || 0) << 16) |
        ((u8[i + 2] || 0) << 8) |
        (u8[i + 3] || 0)
      );
    }
    return CryptoJS.lib.WordArray.create(words, u8.length);
  }
  static _sha256(s) { return CryptoJS.SHA256(s); }

  static decryptFernetToken(tokenB64Url, derivedKeyWA) {
    const keyU8 = this._waToU8(derivedKeyWA);
    if (keyU8.length !== 32) throw new Error('KDF_LEN_NOT_32');

    const signingKey = keyU8.slice(0, 16);
    const encKey     = keyU8.slice(16);

    const tokenB64 = this._b64urlToB64(tokenB64Url);
    const tokenWA  = this._b64ToWA(tokenB64);
    const tokenU8  = this._waToU8(tokenWA);

    if (tokenU8.length < 1 + 8 + 16 + 32) throw new Error('FERNET_TOKEN_TOO_SHORT');

    const version = tokenU8[0];
    if (version !== 0x80) throw new Error('FERNET_BAD_VERSION');

    const hmacOffset = tokenU8.length - 32;
    const macDataU8  = tokenU8.slice(0, hmacOffset);
    const hmacTagU8  = tokenU8.slice(hmacOffset);

    // HMAC(signingKey, version||ts||iv||ct)
    const macDataWA  = this._u8ToWA(macDataU8);
    const signingWA  = this._u8ToWA(signingKey);
    const hmacWA     = CryptoJS.HmacSHA256(macDataWA, signingWA);
    const hmacCalcU8 = this._waToU8(hmacWA);

    if (hmacCalcU8.length !== hmacTagU8.length) throw new Error('FERNET_HMAC_LEN');
    let ok = 1;
    for (let i = 0; i < hmacTagU8.length; i++) ok &= (hmacCalcU8[i] === hmacTagU8[i]) ? 1 : 0;
    if (!ok) throw new Error('FERNET_HMAC_FAIL');

    // AES-CBC(PKCS7) with encKey + iv (bytes 9..25)
    const ivU8 = tokenU8.slice(9, 25);
    const ctU8 = tokenU8.slice(25, hmacOffset);

    const ptWA = CryptoJS.AES.decrypt(
      { ciphertext: this._u8ToWA(ctU8) },
      this._u8ToWA(encKey),
      { iv: this._u8ToWA(ivU8), mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 }
    );

    const ptU8 = this._waToU8(ptWA);
    if (!ptU8 || ptU8.length === 0) throw new Error('FERNET_AES_EMPTY'); // decryption gave nothing
    return ptU8;
  }

  static decryptChunk(encryptedChunkB64Url, viewerSessionKey) {
    const alg = (DOCUMENT_VIEWER_CONFIG.DOC_ENC_ALG || 'fernet').toLowerCase();

    if (alg === 'none') {
      const b64 = this._b64urlToB64(encryptedChunkB64Url);
      return this._waToU8(CryptoJS.enc.Base64.parse(b64));
    }

    if (alg !== 'fernet') {
      throw new Error(`ALG_UNSUPPORTED:${DOCUMENT_VIEWER_CONFIG.DOC_ENC_ALG}`);
    }

    if (!viewerSessionKey) {
      throw new Error('VIEWER_SESSION_KEY_MISSING');
    }

    const keyB64 = this._b64urlToB64(viewerSessionKey);
    const sessionKeyWA = this._b64ToWA(keyB64);
    if (this._waToU8(sessionKeyWA).length !== 32) {
      throw new Error('VIEWER_SESSION_KEY_INVALID');
    }

    return this.decryptFernetToken(encryptedChunkB64Url, sessionKeyWA);
  }

  static assembleChunks(parts) {
    const total = parts.reduce((n, c) => n + c.length, 0);
    const out = new Uint8Array(total);
    let off = 0;
    for (const c of parts) { out.set(c, off); off += c.length; }
    return out;
  }
}

export default ChunkDecryptionService;
